import { generatePath, matchPath } from "react-router-dom";

import {
  EQUIPMENTS,
  EQUIPMENTS_CREATE,
  EXPENSE,
  EXPENSE_CREATE,
  FUEL_HISTORY,
  FUEL_HISTORY_CREATE,
  INSPECTIONS_FORMS,
  INSPECTIONS_FORMS_ADD,
  ISSUES,
  ISSUES_ADD,
  PARTS,
  PARTS_CREATE,
  PURCHASE_ORDERS,
  PURCHASE_ORDERS_CREATE,
  REMINDERS_CONTACTS,
  REMINDERS_CONTACTS_ADD,
  REMINDERS_SERVICES,
  REMINDERS_SERVICES_CREATE,
  REMINDERS_VEHICLES,
  REMINDERS_VEHICLES_CREATE,
  SCHEDULES,
  SERVICE_HISTORY,
  SERVICE_HISTORY_CREATE,
  SERVICE_TASKS,
  SERVICE_TASKS_CREATE,
  UNITS,
  USERS,
  USERS_CREATE,
  WARRANTY,
  WARRANTY_СREATE,
  WORK_ORDERS,
  WORK_ORDERS_ADD,
} from "Shared/Constants/routes";

export type RouteDrawerConfig = {
  /** The form's route; it renders inside the drawer, never as a page. */
  create: string;
  /** The page behind the drawer, whose URL carries `?new=1`. */
  parent: string;
  title: string;
  /** Sized to the form: single-column forms are narrow, split ones wide. */
  width: number;
};

const DRAWERS: RouteDrawerConfig[] = [
  { create: `${UNITS}/create/*`, parent: UNITS, title: "New vehicle", width: 900 },
  { create: USERS_CREATE, parent: USERS, title: "New user", width: 760 },
  { create: `${ISSUES_ADD}/:id`, parent: ISSUES, title: "New issue", width: 760 },
  { create: ISSUES_ADD, parent: ISSUES, title: "New issue", width: 760 },
  {
    create: PURCHASE_ORDERS_CREATE,
    parent: PURCHASE_ORDERS,
    title: "New purchase order",
    width: 1000,
  },
  { create: PARTS_CREATE, parent: PARTS, title: "New part", width: 880 },
  {
    create: `${SERVICE_HISTORY_CREATE}/:id`,
    parent: SERVICE_HISTORY,
    title: "New service entry",
    width: 1000,
  },
  {
    create: SERVICE_HISTORY_CREATE,
    parent: SERVICE_HISTORY,
    title: "New service entry",
    width: 1000,
  },
  {
    create: SERVICE_TASKS_CREATE,
    parent: SERVICE_TASKS,
    title: "New service task",
    width: 600,
  },
  {
    // `/work-orders/add/:id` edits an existing order; it stays a page.
    create: WORK_ORDERS_ADD,
    parent: WORK_ORDERS,
    title: "New work order",
    width: 1240,
  },
  {
    create: INSPECTIONS_FORMS_ADD,
    parent: INSPECTIONS_FORMS,
    title: "New inspection form",
    width: 600,
  },
  {
    create: `${SCHEDULES}/:id/create`,
    parent: `${INSPECTIONS_FORMS}/:id`,
    title: "New inspection schedule",
    width: 880,
  },
  {
    create: EQUIPMENTS_CREATE,
    parent: EQUIPMENTS,
    title: "New equipment",
    width: 880,
  },
  {
    create: FUEL_HISTORY_CREATE,
    parent: FUEL_HISTORY,
    title: "New fuel entry",
    width: 720,
  },
  {
    create: REMINDERS_CONTACTS_ADD,
    parent: REMINDERS_CONTACTS,
    title: "New contact renewal reminder",
    width: 680,
  },
  {
    create: REMINDERS_SERVICES_CREATE,
    parent: REMINDERS_SERVICES,
    title: "New service reminder",
    width: 1000,
  },
  {
    create: REMINDERS_VEHICLES_CREATE,
    parent: REMINDERS_VEHICLES,
    title: "New vehicle renewal reminder",
    width: 680,
  },
  { create: EXPENSE_CREATE, parent: EXPENSE, title: "New expense", width: 760 },
  { create: WARRANTY_СREATE, parent: WARRANTY, title: "New warranty", width: 960 },
];

const trimSlash = (path: string) =>
  path.length > 1 ? path.replace(/\/+$/, "") : path;

const paramNames = (pattern: string) =>
  (pattern.match(/:\w+/g) ?? []).map((name) => name.slice(1));

const hasSplat = (pattern: string) => pattern.endsWith("/*");

/** Route params the form has but its list page doesn't (`new_<name>`). */
const extraParams = (config: RouteDrawerConfig) => {
  const parent = paramNames(config.parent);
  return paramNames(config.create).filter((name) => !parent.includes(name));
};

// Most specific first, so `/issues?new=1&new_id=7` beats `/issues?new=1`.
export const ROUTE_DRAWERS = [...DRAWERS].sort(
  (a, b) => extraParams(b).length - extraParams(a).length,
);

const DRAWER_PARAM = "new";
const isDrawerParam = (key: string) =>
  key === DRAWER_PARAM || key.startsWith(`${DRAWER_PARAM}_`);

const searchWithout = (params: URLSearchParams) => {
  const rest = new URLSearchParams();
  params.forEach((value, key) => {
    if (!isDrawerParam(key)) rest.append(key, value);
  });
  const text = rest.toString();
  return text ? `?${text}` : "";
};

/**
 * `/fuel-history/create?vehicle_id=7` → `/fuel-history?new=1&vehicle_id=7`.
 * Null when the path isn't a create form.
 */
export const toDrawerUrl = (pathname: string, search = ""): string | null => {
  const path = trimSlash(pathname);
  for (const config of ROUTE_DRAWERS) {
    const match = matchPath({ path: trimSlash(config.create), end: true }, path);
    if (!match) continue;
    const parentParams = Object.fromEntries(
      paramNames(config.parent).map((name) => [name, match.params[name]]),
    );
    const query = new URLSearchParams();
    // A wizard step (`/units/create/financial`) rides in `new` itself.
    query.set(DRAWER_PARAM, (hasSplat(config.create) && match.params["*"]) || "1");
    extraParams(config).forEach((name) =>
      query.set(`${DRAWER_PARAM}_${name}`, match.params[name] ?? ""),
    );
    new URLSearchParams(search).forEach((value, key) => {
      if (!isDrawerParam(key)) query.append(key, value);
    });
    return `${generatePath(config.parent, parentParams)}?${query}`;
  }
  return null;
};

export type RouteDrawerMatch = {
  config: RouteDrawerConfig;
  /** The list or detail page behind the drawer. */
  parentPath: string;
  /** Where the form renders inside the drawer. */
  formPath: string;
  /** The URL's other params, which prefill the form (`vehicle_id`, …). */
  formSearch: string;
};

/** The open drawer for a URL like `/fuel-history?new=1`, if any. */
export const findRouteDrawer = (
  pathname: string,
  search = "",
): RouteDrawerMatch | null => {
  const query = new URLSearchParams(search);
  const value = query.get(DRAWER_PARAM);
  if (!value) return null;
  const path = trimSlash(pathname);
  for (const config of ROUTE_DRAWERS) {
    const match = matchPath({ path: trimSlash(config.parent), end: true }, path);
    if (!match) continue;
    const extras = extraParams(config);
    if (extras.some((name) => !query.get(`${DRAWER_PARAM}_${name}`))) continue;
    const params: Record<string, string> = { ...(match.params as Record<string, string>) };
    extras.forEach((name) => {
      params[name] = query.get(`${DRAWER_PARAM}_${name}`) ?? "";
    });
    if (hasSplat(config.create)) params["*"] = value === "1" ? "" : value;
    return {
      config,
      parentPath: path,
      formPath: trimSlash(generatePath(config.create, params)),
      formSearch: searchWithout(query),
    };
  }
  return null;
};

export const isSamePath = (a: string, b: string) =>
  trimSlash(a) === trimSlash(b);
